export interface IceFieldMapping {
  path: string
  label: string
}

export interface IceInstrumentFieldMapping {
  scheme: string
  label: string
  instrumentPath: string
}

export const COMMON_FIELDS: readonly IceFieldMapping[] = [
  { path: 'ID_EVENT', label: 'Event ID' },
  { path: 'ID_REVISION', label: 'Revision ID' },
  { path: 'event_type', label: 'Event Type' },
  { path: 'action_type', label: 'Action Type' },
  { path: 'entry_date', label: 'Entry Date' },
  { path: 'effective_date', label: 'Effective Date' },
]

export const COMMON_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] = [
  { scheme: 'ISIN', label: 'ISIN', instrumentPath: 'instrument' },
  { scheme: 'CUSIP', label: 'CUSIP', instrumentPath: 'instrument' },
  { scheme: 'SEDOL', label: 'SEDOL', instrumentPath: 'instrument' },
]

export const MA_FIELDS: readonly IceFieldMapping[] = [
  {
    path: 'merger_acquisition.offer_cash_amount.currency',
    label: 'Offer Currency',
  },
  {
    path: 'merger_acquisition.offer_cash_amount.value',
    label: 'Offer Cash Amount',
  },
  {
    path: 'merger_acquisition.offered_security_details.offered_security_amount',
    label: 'Offered Security Amount',
  },
]

export const MA_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] = [
  {
    scheme: 'ISIN',
    label: 'Destination ISIN',
    instrumentPath:
      'merger_acquisition.offered_security_details.offered_instrument',
  },
]

// AMET0-3440 slice 4b: Rights Offering fields migrated to subtypeOverrides (Option A).
//
// Base fields carry the AMET0-3381 baseline paths shared by all Capital Change
// subtypes, plus event_market_details fields (ex_date, payment_date) which are
// general Capital Change fields present on many subtypes (Forward Split, etc.).
// Truly RO-specific paths (issue_price.*, capital_event_marker, record_date,
// expiration_date) live exclusively in the 'rights offering' subtype override
// below. Non-RO CC subtypes resolve to this base entry and render only these
// 7 fields — no empty RO rows.
export const CAPITAL_CHANGE_FIELDS: readonly IceFieldMapping[] = [
  // --- AMET0-3381 baseline fields (all Capital Change subtypes) ---
  {
    path: 'capital_change.capital_change_type',
    label: 'Capital Change Type',
  },
  {
    path: 'capital_change.adjustment_factor',
    label: 'Adjustment Factor',
  },
  {
    path: 'capital_change.capital_change_details.capital_change_subtype',
    label: 'Sub-type',
  },
  {
    path: 'capital_change.capital_change_details.no_new_securities',
    label: 'New Securities',
  },
  {
    path: 'capital_change.capital_change_details.no_old_securities',
    label: 'Old Securities',
  },
  // --- General Capital Change fields (event_market_details, present on all subtypes) ---
  // Note: event_market_details may contain multiple entries; v1 renders [0] only.
  // Entries at index 1+ are silently dropped — known design smell, deferred.
  { path: 'event_market_details[0].ex_date', label: 'Ex Date' },
  { path: 'event_market_details[0].payment_date', label: 'Payment Date' },
]

export const CAPITAL_CHANGE_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  [
    {
      scheme: 'ISIN',
      label: 'Destination ISIN',
      instrumentPath:
        'capital_change.capital_change_details.payment_instrument',
    },
  ]

// NOTE on masquerading paths in REPURCHASE_FIELDS:
// ICE places single-option data at the family root and multi-option data under
// repurchase.repurchase_details[i]. The paths below that share the
// 'repurchase.repurchase_details' prefix (i.e. offered_security_amount) are
// option-level fields flattened into the family object for the single-option /
// mandatory shape. They are intentionally NOT in eventLevelOnlyFields.
// Similarly, repurchase.payment_amount.* and repurchase.unit_of_offer_amount
// are option-level fields masquerading at family root — not event-level.
// Only repurchase.repurchase_type and expiration_date are genuinely event-level.
export const REPURCHASE_FIELDS: readonly IceFieldMapping[] = [
  { path: 'repurchase.repurchase_type', label: 'Repurchase Type' },
  {
    path: 'repurchase.payment_amount.currency',
    label: 'Payment Currency',
  },
  { path: 'repurchase.payment_amount.value', label: 'Payment Amount' },
  { path: 'repurchase.unit_of_offer_amount', label: 'Unit of Offer Amount' },
  { path: 'expiration_date', label: 'Expiration Date' },
  {
    path: 'repurchase.repurchase_details.offered_security_amount',
    label: 'Offered Security Amount',
  },
]

export const REPURCHASE_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  [
    {
      scheme: 'ISIN',
      label: 'Destination ISIN',
      instrumentPath: 'repurchase.repurchase_details.offered_instrument',
    },
  ]

export const DIVIDEND_FIELDS: readonly IceFieldMapping[] = [
  { path: 'dividend.dividend_type', label: 'Dividend Type' },
  {
    path: 'dividend.security_payment_rate',
    label: 'Security Payment Rate',
  },
  { path: 'dividend.payment_basis', label: 'Payment Basis' },
  { path: 'event_market_details[0].ex_date', label: 'Ex Date' },
  { path: 'event_market_details[0].payment_date', label: 'Payment Date' },
]

export const DIVIDEND_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  []

export const IDENTIFIER_CHANGES_FIELDS: readonly IceFieldMapping[] = [
  { path: 'identifier_change.identifier_type', label: 'Identifier Type' },
  { path: 'identifier_change.identifier_value', label: 'Identifier Value' },
  {
    path: 'identifier_change.new_identifier_value',
    label: 'New Identifier Value',
  },
]

export const IDENTIFIER_CHANGES_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  []

// AMET0-3439: Redemption field mapping.
// Mandatory Full Call is the primary shape; all fields live directly under the
// `redemption` family object (no options array). Voluntary Redemption / callable
// bonds with optionality are out of scope for this case — do not add
// optionsArrayKey here until a follow-up case is filed for that shape.
export const REDEMPTION_FIELDS: readonly IceFieldMapping[] = [
  { path: 'redemption.redemption_type', label: 'Redemption Type' },
  { path: 'redemption.redemption_date', label: 'Redemption Date' },
  { path: 'redemption.redemption_price.value', label: 'Redemption Price' },
  {
    path: 'redemption.redemption_price.currency',
    label: 'Redemption Currency',
  },
  { path: 'redemption.accrued_interest', label: 'Accrued Interest' },
  {
    path: 'redemption.accrued_interest_marker',
    label: 'Accrued Interest Marker',
  },
  { path: 'redemption.state.code', label: 'State' },
]

// Redemption has no destination instrument — the redeemed security is the subject
// instrument (already covered by COMMON_INSTRUMENT_FIELDS).
export const REDEMPTION_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  []

export const OPTION_BEARING_ACTION_TYPES = [
  'Voluntary',
  'Mandatory with Choice',
] as const
export type OptionBearingActionType = typeof OPTION_BEARING_ACTION_TYPES[number]

export const MA_OPTION_FIELDS: readonly IceFieldMapping[] = [
  { path: 'offer_cash_amount.value', label: 'Offer Cash Amount' },
  { path: 'offer_cash_amount.currency', label: 'Offer Currency' },
  {
    path: 'offered_security_details.offered_security_amount',
    label: 'Offered Security Amount',
  },
]

export const MA_OPTION_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  [
    {
      scheme: 'ISIN',
      label: 'Offered Security',
      instrumentPath: 'offered_security_details.offered_instrument',
    },
  ]

export const TENDER_OFFER_FIELDS: readonly IceFieldMapping[] = [
  { path: 'tender_offer.offer_cash_amount.currency', label: 'Offer Currency' },
  { path: 'tender_offer.offer_cash_amount.value', label: 'Offer Cash Amount' },
  {
    path: 'tender_offer.offered_security_details.offered_security_amount',
    label: 'Offered Security Amount',
  },
  { path: 'expiration_date', label: 'Expiration Date' },
]

export const TENDER_OFFER_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  [
    {
      scheme: 'ISIN',
      label: 'Destination ISIN',
      instrumentPath:
        'tender_offer.offered_security_details.offered_instrument',
    },
  ]

export const TENDER_OPTION_FIELDS: readonly IceFieldMapping[] = [
  { path: 'offer_cash_amount.value', label: 'Offer Cash Amount' },
  { path: 'offer_cash_amount.currency', label: 'Offer Currency' },
  {
    path: 'offered_security_details.offered_security_amount',
    label: 'Offered Security Amount',
  },
]

export const TENDER_OPTION_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  [
    {
      scheme: 'ISIN',
      label: 'Offered Security',
      instrumentPath: 'offered_security_details.offered_instrument',
    },
  ]

// AMET0-3440: Per-group fields for multi-option Capital Change rendering.
// Paths are relative to each option element (capital_change.capital_change_details[i]).
//
// Dual-residence fields (also in CAPITAL_CHANGE_FIELDS at event-level dotted paths):
//   - issue_price.value / issue_price.currency — see the dual-residence note on
//     CAPITAL_CHANGE_FIELDS for why both arrays carry these.
//   - no_new_securities / no_old_securities — these genuinely differ per-option
//     in multi-option Rights Offering (e.g., Option 1: 0.095/1, Option 2: 1/1).
//     The event-level dotted path walks into an array → undefined in multi-option
//     mode, so the per-option entries here are required to surface the values.
//     Single-option mode routes through flat [0]-promotion and resolves them via
//     CAPITAL_CHANGE_FIELDS — no double-render.
export const CAPITAL_CHANGE_OPTION_FIELDS: readonly IceFieldMapping[] = [
  { path: 'capital_change_subtype', label: 'Capital Change Subtype' },
  { path: 'payment_amount.value', label: 'Payment Amount' },
  { path: 'payment_amount.currency', label: 'Payment Currency' },
  { path: 'payment_value.value', label: 'Payment Value' },
  { path: 'payment_value.currency', label: 'Payment Value Currency' },
  { path: 'no_new_securities', label: 'New Securities' },
  { path: 'no_old_securities', label: 'Old Securities' },
  { path: 'issue_price.value', label: 'Issue Price' },
  { path: 'issue_price.currency', label: 'Issue Price Currency' },
]

export const CAPITAL_CHANGE_OPTION_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  [
    {
      scheme: 'ISIN',
      label: 'Payment Instrument',
      instrumentPath: 'payment_instrument',
    },
  ]

export const REPURCHASE_OPTION_FIELDS: readonly IceFieldMapping[] = [
  { path: 'payment_amount.value', label: 'Payment Amount' },
  { path: 'payment_amount.currency', label: 'Payment Currency' },
  { path: 'unit_of_offer_amount', label: 'Unit of Offer Amount' },
  { path: 'offered_security_amount', label: 'Offered Security Amount' },
]

export const REPURCHASE_OPTION_INSTRUMENT_FIELDS: readonly IceInstrumentFieldMapping[] =
  [
    {
      scheme: 'ISIN',
      label: 'Offered Security',
      instrumentPath: 'offered_instrument',
    },
  ]

export interface EventTypeEntry {
  fields: readonly IceFieldMapping[]
  instrumentFields: readonly IceInstrumentFieldMapping[]
  subtypePath?: string
  // Option-bearing additions (AMET0-3438)
  optionsArrayKey?: string
  optionFields?: readonly IceFieldMapping[]
  optionInstrumentFields?: readonly IceInstrumentFieldMapping[]
  /**
   * Fields from `fields` that live at the event root (not under optionsArrayKey) and
   * must therefore appear in eventLevelRows even in options mode.
   *
   * Invariant (enforced by unit test):
   *   - Every path here must also appear in `fields` (subset).
   *   - No path here shares a root segment with `optionsArrayKey` (disjoint).
   *
   * Only populated for families where ICE places some `fields` paths at the event root
   * rather than under the options array. M&A omits this property because every MA_FIELDS
   * path is scoped under the `merger_acquisition` key, which is also the optionsArrayKey —
   * i.e., all M&A per-family fields are option-level and none are event-level.
   */
  eventLevelOnlyFields?: readonly IceFieldMapping[]
  /**
   * AMET0-3440 slice 4a — per-subtype override map.
   *
   * Keys are **lowercase** subtype values (matching the `event_type_lc` convention).
   * The subtype value is read at `subtypePath`, string-coerced, and lowercased before
   * lookup. Requires `subtypePath` to be declared on the entry (enforced by runtime
   * self-test; a TypeScript discriminated-union form was considered but rejected
   * because it would require every `EventTypeEntry` literal to explicitly set
   * `subtypeOverrides: undefined`, making the diff in 3438 and elsewhere noisy).
   *
   * **Merge contract (shallow replace — no deep merge):**
   * - When the payload's subtype matches a key, the override's declared fields
   *   **replace** the corresponding base entry fields at the top level:
   *   `{ ...baseEntry, ...override }`. No concatenation, no recursive merge.
   * - When the subtype value doesn't match any key, fall back to the base entry as-is.
   * - Missing / null / empty subtype value → fall back to base.
   * - `subtypeOverrides` with an empty `{}` → fall back to base.
   *
   * **Replace-semantics footgun:** if an override declares `fields` but not
   * `eventLevelOnlyFields`, the base's `eventLevelOnlyFields` flows through and may
   * violate `⊆ resolved.fields`. A self-test invariant in `ice-field-mappings.spec.ts`
   * catches this combination — always redeclare `eventLevelOnlyFields` when overriding
   * `fields`.
   */
  subtypeOverrides?: Record<string, Partial<EventTypeEntry>>
}

// Keys must be lowercase — extractIceDetailRows normalizes event_type via
// .trim().toLowerCase() before lookup, so 'Capital Change' → 'capital change'.
//
// Lookup is exact-match-first, then startsWith-prefix fallback (see registryLookup
// in ice-field-extractor.ts). Family-level keys like 'tender offer' intentionally
// match variants such as 'tender offer buyback' and 'tender offer rights'. Keys
// MUST therefore form a non-overlapping prefix set; a self-consistency unit test
// guards this invariant.
export const EVENT_TYPE_FIELD_MAP: Record<string, EventTypeEntry> = {
  'merger acquisition': {
    fields: MA_FIELDS,
    instrumentFields: MA_INSTRUMENT_FIELDS,
    subtypePath: 'merger_acquisition.ma_type',
    optionsArrayKey: 'merger_acquisition',
    optionFields: MA_OPTION_FIELDS,
    optionInstrumentFields: MA_OPTION_INSTRUMENT_FIELDS,
  },
  'tender offer': {
    fields: TENDER_OFFER_FIELDS,
    instrumentFields: TENDER_OFFER_INSTRUMENT_FIELDS,
    optionsArrayKey: 'tender_offer',
    optionFields: TENDER_OPTION_FIELDS,
    optionInstrumentFields: TENDER_OPTION_INSTRUMENT_FIELDS,
    eventLevelOnlyFields: [
      { path: 'expiration_date', label: 'Expiration Date' },
    ],
  },
  'capital change': {
    fields: CAPITAL_CHANGE_FIELDS,
    instrumentFields: CAPITAL_CHANGE_INSTRUMENT_FIELDS,
    subtypePath: 'capital_change.capital_change_type',
    optionsArrayKey: 'capital_change.capital_change_details',
    optionFields: CAPITAL_CHANGE_OPTION_FIELDS,
    optionInstrumentFields: CAPITAL_CHANGE_OPTION_INSTRUMENT_FIELDS,
    // Base entry eventLevelOnlyFields: paths from base fields that are event-level
    // (not per-option) and must appear in eventLevelRows even in options mode.
    eventLevelOnlyFields: [
      {
        path: 'capital_change.capital_change_type',
        label: 'Capital Change Type',
      },
      {
        path: 'capital_change.adjustment_factor',
        label: 'Adjustment Factor',
      },
      // General Capital Change event_market_details fields — present on many subtypes.
      { path: 'event_market_details[0].ex_date', label: 'Ex Date' },
      { path: 'event_market_details[0].payment_date', label: 'Payment Date' },
    ],
    // AMET0-3440 slice 4b — Rights Offering subtype override (Option A).
    //
    // Replace semantics: override.fields fully replaces base.fields for RO payloads.
    // The override redeclares all 7 base paths (5 baseline + 2 general CC) plus the
    // 5 truly RO-specific paths (issue_price.*, capital_event_marker, record_date,
    // expiration_date) = 12 total. Non-RO subtypes (Optional Dividend, Forward Split,
    // etc.) fall back to the base entry and render only the 7 base fields.
    //
    // issue_price.* is in override.fields (flat-mode event-level for single-option RO
    // after [0]-promotion) and also in CAPITAL_CHANGE_OPTION_FIELDS (per-group for
    // multi-option). No double-render: flat mode and options mode are mutually exclusive
    // (gated at length > 1). issue_price.* is intentionally absent from
    // override.eventLevelOnlyFields to prevent double-render in options mode.
    subtypeOverrides: {
      'rights offering': {
        fields: [
          // --- AMET0-3381 baseline (redeclared per replace semantics) ---
          {
            path: 'capital_change.capital_change_type',
            label: 'Capital Change Type',
          },
          {
            path: 'capital_change.adjustment_factor',
            label: 'Adjustment Factor',
          },
          {
            path: 'capital_change.capital_change_details.capital_change_subtype',
            label: 'Sub-type',
          },
          {
            path: 'capital_change.capital_change_details.no_new_securities',
            label: 'New Securities',
          },
          {
            path: 'capital_change.capital_change_details.no_old_securities',
            label: 'Old Securities',
          },
          // --- AMET0-3440 Rights Offering fields ---
          {
            path: 'capital_change.capital_change_details.issue_price.value',
            label: 'Issue Price',
          },
          {
            path: 'capital_change.capital_change_details.issue_price.currency',
            label: 'Issue Price Currency',
          },
          {
            path: 'capital_change.capital_event_marker',
            label: 'Rights Marker',
          },
          { path: 'record_date', label: 'Record Date' },
          { path: 'expiration_date', label: 'Expiration Date' },
          // Note: event_market_details may contain multiple entries; v1 renders [0] only.
          // Entries at index 1+ are silently dropped — known design smell, deferred.
          { path: 'event_market_details[0].ex_date', label: 'Ex Date' },
          {
            path: 'event_market_details[0].payment_date',
            label: 'Payment Date',
          },
        ],
        eventLevelOnlyFields: [
          // Baseline paths that are event-level (not per-option) — redeclared per
          // replace semantics so options mode renders them at event level for multi-option RO.
          {
            path: 'capital_change.capital_change_type',
            label: 'Capital Change Type',
          },
          {
            path: 'capital_change.adjustment_factor',
            label: 'Adjustment Factor',
          },
          // RO-specific event-level paths. issue_price.* intentionally absent —
          // it appears per-option via CAPITAL_CHANGE_OPTION_FIELDS and must not
          // double-render in options mode.
          {
            path: 'capital_change.capital_event_marker',
            label: 'Rights Marker',
          },
          { path: 'record_date', label: 'Record Date' },
          { path: 'expiration_date', label: 'Expiration Date' },
          { path: 'event_market_details[0].ex_date', label: 'Ex Date' },
          {
            path: 'event_market_details[0].payment_date',
            label: 'Payment Date',
          },
        ],
      },
    },
  },
  repurchase: {
    fields: REPURCHASE_FIELDS,
    instrumentFields: REPURCHASE_INSTRUMENT_FIELDS,
    subtypePath: 'repurchase.repurchase_type',
    optionsArrayKey: 'repurchase.repurchase_details',
    optionFields: REPURCHASE_OPTION_FIELDS,
    optionInstrumentFields: REPURCHASE_OPTION_INSTRUMENT_FIELDS,
    eventLevelOnlyFields: [
      { path: 'repurchase.repurchase_type', label: 'Repurchase Type' },
      { path: 'expiration_date', label: 'Expiration Date' },
    ],
  },
  dividend: {
    fields: DIVIDEND_FIELDS,
    instrumentFields: DIVIDEND_INSTRUMENT_FIELDS,
    subtypePath: 'dividend.dividend_type',
  },
  'identifier changes': {
    fields: IDENTIFIER_CHANGES_FIELDS,
    instrumentFields: IDENTIFIER_CHANGES_INSTRUMENT_FIELDS,
    subtypePath: 'identifier_change.identifier_type',
  },
  redemption: {
    fields: REDEMPTION_FIELDS,
    instrumentFields: REDEMPTION_INSTRUMENT_FIELDS,
    subtypePath: 'redemption.redemption_type',
  },
}
